import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines, FadeIn } from "./Motion";
import { CtaRow } from "./Cta";
import { EASE } from "@/lib/site";

export const PageHero = ({ crumb, title, sub, paras, ctas, visual, testid = "page-hero" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} data-testid={testid} className="grain relative overflow-hidden bg-ink pb-20 pt-32 text-white sm:pb-28 sm:pt-40">
      <div aria-hidden="true" className="grid-lines absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]" />
      <div aria-hidden="true" className="absolute right-[-10%] top-[-20%] h-[640px] w-[640px] rounded-full bg-violet-700/25 blur-[140px]" />
      <div className="wrap grid items-end gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <FadeIn className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
            <Link to="/" className="hover:text-white" data-testid="breadcrumb-home">Home</Link>
            {crumb.map((c) => <span key={c} className="flex items-center gap-2"><span>/</span><span className="text-violet-300">{c}</span></span>)}
          </FadeIn>
          <MaskedLines lines={title} testid="page-hero-title" className="mt-8 font-display text-[2.7rem] font-medium leading-[0.96] sm:text-6xl lg:text-7xl xl:text-[5.4rem]" />
          <FadeIn delay={0.6}>
            <p className="mt-8 max-w-2xl font-serif-i text-2xl leading-snug text-violet-200 sm:text-3xl">{sub}</p>
            <div className="mt-6 max-w-xl space-y-4 text-[15px] leading-relaxed text-slate-300 sm:text-base">
              {paras.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <CtaRow className="mt-10">{ctas}</CtaRow>
          </FadeIn>
        </div>
        <motion.div className="relative lg:col-span-5" style={{ y }}>
          <motion.div initial={{ clipPath: "inset(100% 0% 0% 0% round 28px)" }} animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.35 }} className="relative aspect-[4/5] max-h-[560px] w-full overflow-hidden rounded-[28px] sm:aspect-[16/11] lg:aspect-[4/5]">
            {visual}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export const ImageVisual = ({ src, alt, caption }) => (
  <div className="group absolute inset-0">
    <motion.img src={src} alt={alt} initial={{ scale: 1.25 }} animate={{ scale: 1.05 }} transition={{ duration: 2.2, ease: EASE }}
      className="h-full w-full object-cover transition-transform duration-[1600ms] group-hover:scale-110" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0716] via-transparent to-transparent" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,transparent_20%,rgba(11,7,22,0.65)_75%)]" />
    {caption && <p className="absolute bottom-6 left-6 right-6 font-mono text-[11px] uppercase tracking-[0.22em] text-violet-200">{caption}</p>}
  </div>
);
